/**
 * Registers the spreadsheet guest-import CLIENT tools with the AI bridge while
 * an event is open. Like AiTemplateTools these need no page state — only the
 * eventId from the route and the spreadsheet the user attached to the current
 * turn (held in the ai-store's `turnAttachments`). Renders nothing.
 *
 * Two tools, two steps:
 *  - read_attached_spreadsheet → parse the attached .csv/.xlsx in-browser and
 *    return its headers + sample rows + total count so the model can propose a
 *    column→field mapping.
 *  - import_guests_from_spreadsheet → apply the model's mapping, build the guest
 *    rows with the SAME importer machinery as the manual flow (exportGuests /
 *    parseGuest), and submit the IMPORT_GUESTS background job.
 *
 * The file bytes never leave the browser for the model — only the derived
 * headers/sample do. Soft failures are RETURNED as data so the model can
 * self-correct (mirrors AiGuestlistTools §4.5.1).
 */
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAiToolBridge } from "@/lib/ai/ai-tool-bridge";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import {
  parseSpreadsheetToTable,
  spreadsheetExtension,
} from "@/lib/utils/importParse";
import { exportGuests } from "@/lib/utils/importer";
import { collectDroppedEmailCells } from "@/components/PImport/utils/droppedEmailCells";
import {
  extractPartyAliases,
  extractPartyKeys,
} from "@/components/PImport/utils/partyKeys";
import { collectImportErrors } from "@/components/PImport/utils/collectImportErrors";
import { submitBackgroundJob } from "@/lib/api/handlers/BackgroundJobHandling";
import { importLegalBasisParamsFromConfirmation } from "@/lib/legal/importLegalBasis";
import { useBackgroundJobs } from "@/providers/BackgroundJobsProvider";
import { useEventContext } from "@/providers/EventProvider";
import type { ImportMapper } from "@/components/PImport/types/ImportMapper";
import type { TableColumnData } from "@/components/PImport/types/TableColumnData";
import type { Field } from "@/types/Field";
import type { Guest } from "@/types/Guest";
import { DataType } from "@/types/enums/DataType";
import { Gender } from "@/types/enums/Gender";
import { GuestState } from "@/types/enums/GuestState";

/** Synthesize a Field the importer's parseGuest understands (no FieldsProvider
 *  here — these tools are mounted app-wide). */
function stdField(name: string, dataType: DataType, options?: string[]): Field {
  return { name, dataType, additionalField: false, editable: true, options };
}

/** Standard guest fields a column may map to. Anything NOT here is treated as a
 *  custom field and stored in additionalFields (validated server-side on import). */
const STANDARD_FIELDS: Record<string, Field> = {
  firstName: stdField("firstName", DataType.STRING),
  lastName: stdField("lastName", DataType.STRING),
  email: stdField("email", DataType.EMAIL),
  phone: stdField("phone", DataType.STRING),
  mobile: stdField("mobile", DataType.STRING),
  company: stdField("company", DataType.STRING),
  jobTitle: stdField("jobTitle", DataType.STRING),
  title: stdField("title", DataType.STRING),
  address: stdField("address", DataType.STRING),
  address2: stdField("address2", DataType.STRING),
  zipCode: stdField("zipCode", DataType.STRING),
  city: stdField("city", DataType.STRING),
  country: stdField("country", DataType.STRING),
  language: stdField("language", DataType.STRING),
  username: stdField("username", DataType.STRING),
  code: stdField("code", DataType.STRING),
  emailOptIn: stdField("emailOptIn", DataType.BOOLEAN),
  gender: stdField("gender", DataType.SINGLE_SELECT, Object.values(Gender)),
  state: stdField("state", DataType.SINGLE_SELECT, Object.values(GuestState)),
  // The party link: the cell names the row's MAIN GUEST by id, booking code or
  // email. Free text, resolved server-side — never parsed here. A row with a
  // value becomes that guest's companion; a row without one is a main guest.
  mainGuest: stdField("mainGuest", DataType.STRING),
  // The party alias: the label a MAIN-GUEST row gives itself so companions can
  // name it in `mainGuest` ("Familie Müller"). MUST be a standard field here:
  // an unlisted target falls through resolveField as a CUSTOM field, lands in
  // `additionalFields.partyAlias` on every guest and auto-seeds a schema row —
  // the one thing this column must never do. Lifted into `partyAliases`
  // before submit, like `mainGuest`.
  partyAlias: stdField("partyAlias", DataType.STRING),
};

const STANDARD_FIELD_NAMES = Object.keys(STANDARD_FIELDS);

/** Virtual mapping target: one column that holds the WHOLE name. The importer
 *  maps columns 1:1 and can't split, so this target tells the tool to split the
 *  cell into firstName/lastName itself — very common in real exports
 *  ("Nachname, Vorname" or "Vorname Nachname" in a single column). */
const FULL_NAME_TARGET = "fullName";

/** Field names the model may map a column to: the real standard fields plus the
 *  fullName splitter. Custom/unknown names are allowed too (→ additionalFields). */
const MAPPABLE_FIELD_NAMES = [...STANDARD_FIELD_NAMES, FULL_NAME_TARGET];

function resolveField(target: string): Field {
  return (
    STANDARD_FIELDS[target] ?? {
      name: target,
      dataType: DataType.STRING,
      additionalField: true,
      editable: true,
    }
  );
}

/** Split a combined-name cell into first/last. "Nachname, Vorname" (comma) →
 *  firstName=Vorname, lastName=Nachname; "Vorname Nachname" (space) →
 *  firstName=Vorname, lastName=rest; a single token → firstName only. */
function splitFullName(raw: string | null): { firstName?: string; lastName?: string } {
  const s = (raw ?? "").trim();
  if (!s) return {};
  if (s.includes(",")) {
    const [last, ...rest] = s.split(",");
    const first = rest.join(",").trim();
    return { lastName: last.trim() || undefined, firstName: first || undefined };
  }
  const parts = s.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

/** One mapped Yes/No column whose cells were not all a Yes/No. */
type UnrecognisedYesNo = {
  column: string;
  field: string;
  count: number;
  /** Sheet rows (header = 1), the first ten. */
  rows: number[];
  /** Up to three distinct offending values. */
  samples: string[];
};

/**
 * The Yes/No cells this import carries WITHOUT a value: not empty, not a Yes/No
 * the importer reads (`collectImportErrors`, the wizard's own error list), in
 * rows that are actually submitted. Velo has no warning triangle, so the tool
 * result says it instead — for the mail opt-in an unset value is unknown
 * consent, not a NO (audit-14 M-3).
 */
function unrecognisedYesNoCells(
  mappingMap: Map<number, ImportMapper>,
  table: TableColumnData[],
  submitted: (rowIndex: number) => boolean,
): UnrecognisedYesNo[] {
  const byColumn = new Map<string, UnrecognisedYesNo>();
  for (const e of collectImportErrors(mappingMap, table)) {
    if (e.dataType !== DataType.BOOLEAN || !submitted(e.row - 2)) continue;
    const entry = byColumn.get(e.column) ?? {
      column: e.column,
      field: e.field,
      count: 0,
      rows: [],
      samples: [],
    };
    entry.count += 1;
    if (entry.rows.length < 10) entry.rows.push(e.row);
    if (entry.samples.length < 3 && !entry.samples.includes(e.value)) {
      entry.samples.push(e.value);
    }
    byColumn.set(e.column, entry);
  }
  return [...byColumn.values()];
}

/** Identity/content fields used to decide if a built guest is worth importing
 *  (blank-row guard). Deliberately EXCLUDES state/gender, which default-fill. */
const GUEST_CONTENT_FIELDS: (keyof Guest)[] = [
  "firstName", "lastName", "email", "phone", "mobile", "company", "jobTitle",
  "title", "address", "address2", "zipCode", "city", "country", "username", "code",
];

function hasGuestContent(g: Guest): boolean {
  for (const k of GUEST_CONTENT_FIELDS) {
    const v = g[k];
    if (typeof v === "string" && v.trim() !== "") return true;
  }
  const extra = g.additionalFields;
  if (extra && Object.values(extra).some((v) => v != null && String(v).trim() !== "")) {
    return true;
  }
  return false;
}

/** What the MODEL may pass. Deliberately no legal-basis field: the W8 tick
 *  comes only from the ConfirmationCard (the handler's second argument), and
 *  a `legalBasis*` key the model writes into the input is never read. */
type ImportInput = {
  mapping?: Record<string, string>;
  mode?: string;
  importName?: string;
};

export function AiImportTools() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const { addJob } = useBackgroundJobs();
  const { event, eventIsPlaceholder } = useEventContext();
  // Handler identity stays stable (bridge unregister race-guard); the live
  // values are read via refs.
  const eventIdRef = useRef(eventId);
  eventIdRef.current = eventId;
  const addJobRef = useRef(addJob);
  addJobRef.current = addJob;
  const eventNameRef = useRef(event?.name ?? "");
  eventNameRef.current = event?.name ?? "";
  // Excel date cells are read as wall clock in the EVENT zone — the same
  // reading as the manual importer (`useImport`), one parser, one contract.
  // Only the zone of the event the ROUTE names: right after switching events
  // the provider's `event` is still the previous one (`useEventLayoutEvent`
  // keeps it as placeholder data while the next loads), and event A's zone
  // must not read event B's sheet. The signal is the query's own
  // `isPlaceholderData`, handed down by the provider — NOT `event.eventId`:
  // the shell's projection omits `eventId`, so an id comparison is false for
  // every event and silently turned the zone off altogether. While the event
  // is a placeholder the parser keeps its UTC reading, as outside an event
  // scope.
  const eventTimeZone = eventIsPlaceholder ? undefined : event?.timeZone;
  const eventTimeZoneRef = useRef<string | undefined>(eventTimeZone);
  eventTimeZoneRef.current = eventTimeZone;

  // The spreadsheet attached during this import, STAGED across turns. The
  // natural flow is: turn 1 the user attaches the file + "import these"
  // (model reads it and asks to confirm), turn 2 the user replies "yes"
  // (model calls the import). But a text-only "yes" send clears
  // `turnAttachments`, so we'd lose the bytes between turns — stage the File
  // here on read/import so the confirm-then-import flow works.
  const stagedFileRef = useRef<File | null>(null);

  // This component is mounted ONCE under the shared event-layout route, so it
  // does NOT remount when the user switches events — clear the stage on every
  // eventId change so a sheet staged in event A can't be imported into event B.
  useEffect(() => {
    stagedFileRef.current = null;
  }, [eventId]);

  // Likewise drop the stage when the user starts a FRESH conversation in the
  // same event (no eventId change fires) — otherwise a sheet staged in the old
  // chat would be importable in the new one. `bumpChatReset` increments this.
  const chatResetNonce = useAiAvailabilityStore((s) => s.chatResetNonce);
  useEffect(() => {
    stagedFileRef.current = null;
  }, [chatResetNonce]);

  useEffect(() => {
    const { register, unregister } = useAiToolBridge.getState();

    /** The spreadsheet to operate on: the one attached THIS turn if present,
     *  else the one staged earlier in this import conversation. Refreshes the
     *  stage whenever a new sheet is attached. */
    const resolveSpreadsheet = (): File | null => {
      const files = useAiAvailabilityStore.getState().turnAttachments;
      const attached = files.find((f) => spreadsheetExtension(f.name) !== null);
      if (attached) stagedFileRef.current = attached;
      return attached ?? stagedFileRef.current;
    };

    const readAttachedSpreadsheet = async () => {
      const file = resolveSpreadsheet();
      if (!file) {
        return {
          ok: false,
          errorCode: "noSpreadsheet",
          detail:
            "the user didn't attach a .csv/.xlsx this turn — ask them to attach the file",
        };
      }
      let table: TableColumnData[];
      try {
        table = await parseSpreadsheetToTable(file, {
          timeZone: eventTimeZoneRef.current,
        });
      } catch {
        return { ok: false, errorCode: "parseFailed", fileName: file.name };
      }
      if (table.length === 0) {
        return { ok: false, errorCode: "emptySpreadsheet", fileName: file.name };
      }
      const totalRows = table[0]?.values.length ?? 0;
      const headers = table.map((c) => c.header);
      // Surface duplicate headers — the mapping is keyed by header name, so a
      // repeated header is ambiguous (only the last column would be reachable).
      const duplicateHeaders = [
        ...new Set(headers.filter((h, i) => headers.indexOf(h) !== i)),
      ];
      // Clip long cells so the result stays well under the backend's 16k
      // resultJson cap; shrink the sample-row count too if a wide sheet still
      // overflows. (A 400 resultTooLong would brick the read turn otherwise.)
      const clip = (v: string | null) =>
        v != null && v.length > 120 ? `${v.slice(0, 120)}…` : v;
      const buildSamples = (n: number): Record<string, string | null>[] => {
        const out: Record<string, string | null>[] = [];
        for (let i = 0; i < Math.min(n, totalRows); i++) {
          const row: Record<string, string | null> = {};
          table.forEach((c) => {
            row[c.header] = clip(c.values[i] ?? null);
          });
          out.push(row);
        }
        return out;
      };
      const base = {
        ok: true as const,
        fileName: file.name,
        headers,
        totalRows,
        standardFields: MAPPABLE_FIELD_NAMES,
        fieldHints: {
          fullName:
            "use for ONE column containing the whole name (e.g. 'Paus, Lisa' or "
            + "'Lisa Paus'); the tool splits it into firstName/lastName",
          email: "the guest's email address",
          state: "RSVP state (UNCONTACTED/CONTACTED/ACCEPTED/DECLINED/WAITLISTED)",
          mainGuest:
            "use ONLY for a column naming the row's MAIN GUEST (a guest id, "
            + "booking code, email of ANOTHER row/guest, or the partyAlias "
            + "another row declares) — the row then becomes that guest's "
            + "companion. Never map a person's own name here.",
          partyAlias:
            "use ONLY for a column that labels a MAIN-GUEST row so other rows "
            + "can reference it in mainGuest (e.g. 'Familie Müller'); filled on "
            + "main-guest rows, blank on companions. Never a person's name or "
            + "a value to store.",
        },
        ...(duplicateHeaders.length ? { duplicateHeaders } : {}),
      };
      let sampleRows = buildSamples(5);
      while (
        sampleRows.length > 1 &&
        JSON.stringify({ ...base, sampleRows }).length > 14000
      ) {
        sampleRows = sampleRows.slice(0, -1);
      }
      return { ...base, sampleRows };
    };

    const importGuestsFromSpreadsheet = async (
      rawInput: unknown,
      confirmation?: unknown,
    ) => {
      // Welle Z W8: no import without the organiser's legal-basis tick from
      // the ConfirmationCard — threaded here by the provider as the second
      // argument, never taken from the model's input (which may claim it).
      // A soft error, so the model tells the user instead of retrying blind.
      const legalBasis = importLegalBasisParamsFromConfirmation(confirmation);
      if (!legalBasis) return { ok: false, errorCode: "legalBasisNotConfirmed" };

      const input = (rawInput ?? {}) as ImportInput;
      const eventIdNow = eventIdRef.current;
      if (!eventIdNow) return { ok: false, errorCode: "eventUnavailable" };

      const file = resolveSpreadsheet();
      if (!file) return { ok: false, errorCode: "noSpreadsheet" };

      const mapping =
        input.mapping && typeof input.mapping === "object" ? input.mapping : null;
      if (!mapping || Object.keys(mapping).length === 0) {
        return { ok: false, errorCode: "noMapping" };
      }

      let table: TableColumnData[];
      try {
        table = await parseSpreadsheetToTable(file, {
          timeZone: eventTimeZoneRef.current,
        });
      } catch {
        return { ok: false, errorCode: "parseFailed", fileName: file.name };
      }
      if (table.length === 0) {
        return { ok: false, errorCode: "emptySpreadsheet", fileName: file.name };
      }
      const totalRows = table[0]?.values.length ?? 0;

      const headerToIndex = new Map(table.map((c, i) => [c.header, i]));
      const mappingMap = new Map<number, ImportMapper>();
      const fullNameColIdxs: number[] = [];
      const unknownColumns: string[] = [];
      for (const [header, target] of Object.entries(mapping)) {
        if (!target) continue; // skip explicitly-unmapped columns
        const idx = headerToIndex.get(header);
        if (idx === undefined) {
          unknownColumns.push(header);
          continue;
        }
        // fullName is a virtual target handled by splitting below — it must NOT
        // go through exportGuests (resolveField would store it as a custom field).
        if (target === FULL_NAME_TARGET) {
          fullNameColIdxs.push(idx);
          continue;
        }
        mappingMap.set(idx, { field: resolveField(target), value: "" });
      }
      if (unknownColumns.length > 0) {
        return {
          ok: false,
          errorCode: "unknownColumns",
          unknownColumns,
          validColumns: table.map((c) => c.header),
        };
      }
      if (mappingMap.size === 0 && fullNameColIdxs.length === 0) {
        return { ok: false, errorCode: "noValidMapping" };
      }

      const guests = exportGuests(mappingMap, totalRows, table);
      // Split a combined-name column into firstName/lastName. Only fills fields
      // not already set by an explicit firstName/lastName mapping.
      if (fullNameColIdxs.length > 0) {
        const col = table[fullNameColIdxs[0]]?.values ?? [];
        guests.forEach((g, i) => {
          const { firstName, lastName } = splitFullName(col[i] ?? null);
          if (firstName && !g.firstName) g.firstName = firstName;
          if (lastName && !g.lastName) g.lastName = lastName;
        });
      }

      // Drop blank rows: parseGuest fills empty strings for blank cells and
      // `state`/`gender` may default-fill, so an interior empty row would
      // otherwise be submitted as a content-less guest.
      const nonEmpty = guests.filter(hasGuestContent);
      const skippedEmptyRows = guests.length - nonEmpty.length;
      // Both cell summaries below count only the rows actually submitted
      // (after the full-name split and the blank-row filter), so the one
      // result the model reads speaks about one set of rows (seam audit F3).
      const submitted = (i: number) => guests[i] !== undefined && hasGuestContent(guests[i]);
      // E-mail cells the rule refuses are dropped by `parseEmail` — the guest
      // is imported without an address. This lane has no pre-flight screen,
      // so the result says so: how many, and for the first three the sheet
      // row and the rule's reason. Never the address — the result is read by
      // the model, and it carries counts only (cf. `skippedEmptyRows`). A row
      // whose only content was the refused address is skipped as empty — it
      // is not imported at all, so it is not counted here.
      const droppedEmails = collectDroppedEmailCells(mappingMap, table, { submitted });
      const unrecognisedYesNo = unrecognisedYesNoCells(mappingMap, table, submitted);
      if (nonEmpty.length === 0) {
        return { ok: false, errorCode: "noRows" };
      }

      const taskType =
        input.mode === "overwrite"
          ? "IMPORT_GUESTS_OVERWRITE"
          : "IMPORT_GUESTS_CREATE";
      const importName = input.importName?.trim();

      // Party links leave in `partyKeys`, keyed by row index, never on the row
      // — Guest.mainGuest is an ObjectId server-side and an email in it fails
      // at binding. Extracted AFTER the blank-row filter, because the indices
      // must match the array that is actually submitted. Only sent when
      // creating: an overwrite never re-parents an existing guest.
      const partyKeys = extractPartyKeys(
        nonEmpty as unknown as Record<string, unknown>[]
      );
      // Same array, same indices: an alias on row 3 must mean the row the
      // backend sees as row 3.
      const partyAliases = extractPartyAliases(
        nonEmpty as unknown as Record<string, unknown>[]
      );
      const sendsPartyKeys =
        taskType === "IMPORT_GUESTS_CREATE" &&
        Object.keys(partyKeys).length > 0;
      const sendsPartyAliases =
        sendsPartyKeys && Object.keys(partyAliases).length > 0;

      const result = await submitBackgroundJob(eventIdNow, taskType, {
        guests: nonEmpty,
        ...(sendsPartyKeys ? { partyKeys } : {}),
        ...(sendsPartyAliases ? { partyAliases } : {}),
        ...(importName ? { importName } : {}),
        ...legalBasis,
      });
      if (typeof result !== "string") {
        return { ok: false, errorCode: "submitFailed", detail: result.message };
      }
      // Register the job with the background-jobs tracker so the progress bar +
      // Downloads/Import area pick it up — exactly like the manual importer does
      // (PImport). Without this the job runs but the UI shows no progress.
      addJobRef.current({
        jobId: result,
        eventId: eventIdNow,
        eventName: eventNameRef.current,
        taskType,
        totalItems: nonEmpty.length,
      });
      return {
        ok: true,
        jobId: result,
        importedRows: nonEmpty.length,
        ...(skippedEmptyRows ? { skippedEmptyRows } : {}),
        // Tell the organiser: these guests were imported with the value
        // UNSET — for emailOptIn that is no decision, not a NO.
        ...(unrecognisedYesNo.length
          ? {
              unrecognisedYesNo,
              unrecognisedYesNoNote:
                "These cells are not a Yes/No the importer reads; the guests "
                + "were imported with the value unset (for emailOptIn: no "
                + "consent decision — not a No). Tell the user the column, "
                + "the rows and the values.",
            }
          : {}),
        ...(droppedEmails.count > 0
          ? {
              droppedEmailCells: droppedEmails.count,
              droppedEmailExamples: droppedEmails.examples,
            }
          : {}),
        mode: taskType === "IMPORT_GUESTS_OVERWRITE" ? "overwrite" : "create",
      };
    };

    register("read_attached_spreadsheet", readAttachedSpreadsheet);
    // Import mutates the REAL guest list (overwrite even replaces it) with no
    // undo — the only client tool that must pause on the ConfirmationCard
    // before its handler runs.
    register("import_guests_from_spreadsheet", importGuestsFromSpreadsheet, {
      requiresConfirm: true,
    });
    return () => {
      unregister("read_attached_spreadsheet", readAttachedSpreadsheet);
      unregister("import_guests_from_spreadsheet", importGuestsFromSpreadsheet);
    };
  }, []);

  return null;
}
